import { useMemo, useState } from 'react';
import type { Mode } from '../App';
import { generatePrompts, GenerateError } from '../lib/promptEngine';
import type { GenerateResult } from '../lib/promptEngine';
import { FM2_EMOTIONS, FM2_ACTIVITIES, FM2_WORLDS, FM2_REGIONS, emotionToEngineInput } from '../data/feelData';
import { INSTS, SCALE_GROUPS, ATMOSPHERES, BRAINWAVES, FREQS } from '../data/expertData';
import { FieldTooltip } from './FieldTooltip';

// ── Interactive Step Guide ────────────────────────────────────────────────────
const STEP_LABELS: Record<string, { feel: string[]; expert: string[] }> = {
  en: {
    feel: ['Choose a Genre', 'Pick a Mood or type your feeling', 'Choose what people will do while listening', 'Set Calm → Intense level', 'Select platforms', 'Click Generate ✓'],
    expert: ['Choose Raga / Scale', 'Pick all 3 Instruments', 'Set BPM & Reverb', 'Add Binaural (optional)', 'Select platforms', 'Click Generate ✓'],
  },
  hi: {
    feel: ['Genre चुनें', 'Mood चुनें या type करें', 'Activity चुनें', 'Calm → Intense set करें', 'Platforms चुनें', 'Generate दबाएं ✓'],
    expert: ['Raag / Scale चुनें', 'तीनों Instruments चुनें', 'BPM & Reverb set करें', 'Binaural add करें (optional)', 'Platforms चुनें', 'Generate दबाएं ✓'],
  },
  es: {
    feel: ['Elige un Género', 'Elige un Mood o escribe', 'Elige la actividad', 'Ajusta Calma → Intenso', 'Selecciona plataformas', 'Clic en Generar ✓'],
    expert: ['Elige Raga / Escala', 'Elige los 3 Instrumentos', 'Configura BPM y Reverb', 'Agrega Binaural (opcional)', 'Selecciona plataformas', 'Clic en Generar ✓'],
  },
  pt: {
    feel: ['Escolha um Gênero', 'Escolha um Humor ou escreva', 'Escolha a atividade', 'Ajuste Calmo → Intenso', 'Selecione plataformas', 'Clique em Gerar ✓'],
    expert: ['Escolha Raga / Escala', 'Escolha os 3 Instrumentos', 'Configure BPM e Reverb', 'Adicione Binaural (opcional)', 'Selecione plataformas', 'Clique em Gerar ✓'],
  },
  fr: {
    feel: ['Choisir un Genre', 'Choisir un Mood ou taper', 'Choisir l\'activité', 'Régler Calme → Intense', 'Sélectionner les plateformes', 'Cliquer sur Générer ✓'],
    expert: ['Choisir Raga / Gamme', 'Choisir les 3 Instruments', 'Configurer BPM et Reverb', 'Ajouter Binaural (optionnel)', 'Sélectionner les plateformes', 'Cliquer sur Générer ✓'],
  },
  de: {
    feel: ['Genre wählen', 'Stimmung wählen oder tippen', 'Aktivität wählen', 'Ruhig → Intensiv einstellen', 'Plattformen auswählen', 'Auf Generieren klicken ✓'],
    expert: ['Raga / Tonleiter wählen', 'Alle 3 Instrumente wählen', 'BPM & Hall einstellen', 'Binaural hinzufügen (optional)', 'Plattformen auswählen', 'Auf Generieren klicken ✓'],
  },
  ja: {
    feel: ['ジャンルを選ぶ', 'ムードを選ぶか入力', 'アクティビティを選ぶ', '穏やか→強烈を設定', 'プラットフォームを選ぶ', 'Generateをクリック ✓'],
    expert: ['ラーガ/スケールを選ぶ', '楽器を3つ選ぶ', 'BPMとリバーブを設定', 'バイノーラルを追加(任意)', 'プラットフォームを選ぶ', 'Generateをクリック ✓'],
  },
  ar: {
    feel: ['اختر النوع', 'اختر المزاج أو اكتب', 'اختر النشاط', 'اضبط هادئ → مكثّف', 'اختر المنصات', 'انقر توليد ✓'],
    expert: ['اختر الراغ / السلم', 'اختر الآلات الثلاث', 'اضبط BPM والصدى', 'أضف ثنائي الأذن (اختياري)', 'اختر المنصات', 'انقر توليد ✓'],
  },
  ko: {
    feel: ['장르 선택', '무드 선택 또는 입력', '활동 선택', '조용 → 강렬 설정', '플랫폼 선택', 'Generate 클릭 ✓'],
    expert: ['라가/스케일 선택', '악기 3개 선택', 'BPM & 리버브 설정', '바이노럴 추가(선택)', '플랫폼 선택', 'Generate 클릭 ✓'],
  },
};

function getStepLang(): string {
  const lang = (navigator.language || 'en').toLowerCase().split('-')[0];
  return STEP_LABELS[lang] ? lang : 'en';
}

function StepGuide({ mode, steps }: { mode: Mode; steps: boolean[] }) {
  const lang = getStepLang();
  const labels = STEP_LABELS[lang]?.[mode] ?? STEP_LABELS.en[mode];
  const isRTL = lang === 'ar';
  const allDone = steps.every(Boolean);
  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginBottom: '16px',
      direction: isRTL ? 'rtl' : 'ltr',
    }}>
      {labels.map((label, i) => {
        const done = steps[i];
        const isLast = i === labels.length - 1;
        return (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '5px 12px',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: 600,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            transition: 'all 0.3s ease',
            background: done
              ? (isLast && allDone ? 'rgba(34,139,34,0.15)' : 'rgba(34,139,34,0.12)')
              : 'rgba(196,154,60,0.08)',
            border: done
              ? (isLast && allDone ? '1.5px solid rgba(34,139,34,0.5)' : '1.5px solid rgba(34,139,34,0.3)')
              : '1.5px solid rgba(196,154,60,0.2)',
            color: done ? '#1a6e1a' : '#8a7055',
          }}>
            <span style={{
              width: '16px', height: '16px',
              borderRadius: '50%',
              background: done ? '#2a8a2a' : 'rgba(196,154,60,0.2)',
              color: done ? '#fff' : '#a07840',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '9px', fontWeight: 700, flexShrink: 0,
              transition: 'all 0.3s',
            }}>
              {done ? '✓' : i + 1}
            </span>
            {label}
          </div>
        );
      })}
    </div>
  );
}


const PLATFORMS = [
  { id:'suno', name:'Suno', sub:'suno.com · prose prompt', emoji:'🎵' },
  { id:'udio', name:'Udio', sub:'udio.com · tag prompt', emoji:'🎛' },
  { id:'stable', name:'Stable Audio', sub:'stability.ai · detailed text', emoji:'🔊' },
  { id:'mureka', name:'Mureka', sub:'mureka.ai · structured fields', emoji:'🎼' },
  { id:'beatoven', name:'Beatoven', sub:'beatoven.ai · mood+activity', emoji:'🐷' },
  { id:'soundraw', name:'Soundraw', sub:'soundraw.io · tag sliders', emoji:'💧' },
  { id:'loudly', name:'Loudly', sub:'loudly.com · loop music', emoji:'📣' },
  { id:'musicgen', name:'MusicGen', sub:'Meta · short prompt', emoji:'🤖' },
];
type Usage = { plan:'free'|'paid'; gens_used:number; limit:number; isMaster:boolean; } | null;
type OutputTab = 'prompts' | 'youtube' | 'social' | 'binaural' | 'distro' | 'all';

const DEFAULT_INST_A = 'Sitar playing intricate melodic phrases';
const DEFAULT_INST_B = 'Tabla keeping rhythmic cycles';
const DEFAULT_INST_C = 'Tanpura drone sustain';

export default function GeneratorView({ mode, usage, onAfterGenerate }: {
  mode: Mode; usage: Usage; onAfterGenerate: () => void;
}) {
  const [world, setWorld] = useState('fusion');
  const [region, setRegion] = useState('any');
  const [emotionId, setEmotionId] = useState<string | null>(null);
  const [customMood, setCustomMood] = useState('');
  const [activityId, setActivityId] = useState<string | null>(null);
  const [energy, setEnergy] = useState(5);

  function pickEmotion(id: string | null) {
    setEmotionId(id);
    if (!id) return;
    const emo = FM2_EMOTIONS.find(e => e.id === id);
    if (!emo) return;
    const energyByCategory: Record<string, number> = {
      peaceful:2, tender:3, melancholy:3, nostalgic:3, mystical:4, devotional:4,
      haunting:3, romantic:4, loving:4, playful:6, joyful:7, dramatic:7,
      triumphant:8, fierce:9, dark:4,
    };
    const top = Object.entries(emo.scores).sort((a,b) => b[1]-a[1])[0];
    if (top && energyByCategory[top[0]] !== undefined) setEnergy(energyByCategory[top[0]]);
  }

  const [platforms, setPlatforms] = useState<string[]>(PLATFORMS.map(p => p.id));

  const [expert, setExpert] = useState({
    instA: DEFAULT_INST_A,
    instB: DEFAULT_INST_B,
    instC: DEFAULT_INST_C,
    scale:'', atmosphere:'', mood:'', bpm:'', arrangement:'', reverb:'',
    brainwave:'', freq:'', ambient:'', harmonic:'',
    title:'', channel:'', custom_tags:'', scene:'',
    subject:'', benefit:'', listener:'', session_len:'',
  });

  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [outputTab, setOutputTab] = useState<OutputTab>('prompts');
  const [lastInput, setLastInput] = useState<any>(null);
  const [youtubeContent, setYoutubeContent] = useState<string | null>(null);
  const [socialContent, setSocialContent] = useState<string | null>(null);
  const [binauralContent, setBinauralContent] = useState<string | null>(null);
  const [distroContent, setDistroContent] = useState<string | null>(null);
  const [tabBusy, setTabBusy] = useState(false);

  // Pay-gating removed: the tool is free and unlimited. Entitlement data is still
  // fetched (for the Admin dashboard / future use) but never blocks generation here.
  const outOfQuota = false;

  const regionsByGroup = useMemo(() => {
    const g: Record<string, typeof FM2_REGIONS> = {};
    FM2_REGIONS.forEach(r => { const k = r.group || '—'; (g[k] = g[k] || []).push(r); });
    return g;
  }, []);
  const instsByGroup = useMemo(() => {
    const g: Record<string, Inst[]> = {};
    INSTS.forEach(i => { (g[i.g] = g[i.g] || []).push(i); });
    return g;
  }, []);

  function togglePlatform(id: string) { setPlatforms(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }
  function surprise() {
    const e = FM2_EMOTIONS[Math.floor(Math.random() * FM2_EMOTIONS.length)];
    const a = FM2_ACTIVITIES[Math.floor(Math.random() * FM2_ACTIVITIES.length)];
    const w = FM2_WORLDS[Math.floor(Math.random() * FM2_WORLDS.length)];
    pickEmotion(e.id); setActivityId(a.id); setWorld(w.id);
  }

  async function onGenerate() {
    setBusy(true); setError(null); setResult(null);
    setYoutubeContent(null); setSocialContent(null);
    setBinauralContent(null); setDistroContent(null);
    setOutputTab('prompts');
    try {
      let input: any;
      if (mode === 'feel') {
        const emo = FM2_EMOTIONS.find(e => e.id === emotionId);
        if (!emo && !customMood.trim()) { setError('Pick a feeling card or type a mood.'); setBusy(false); return; }
        const w = FM2_WORLDS.find(x => x.id === world) || FM2_WORLDS[0];
        const act = FM2_ACTIVITIES.find(a => a.id === activityId) || null;
        if (emo) {
          input = { ...emotionToEngineInput(emo, act, w, region === 'any' ? '' : region, energy),
            freq:'', ambient:'', harmonic:'', brainwave:'',
            benefit:'', listener:'', session_len:'', title:'', channel:'', custom_tags:'',
            platforms };
        } else {
          input = { instA:'', instB:'', instC:'', mood: customMood, scale:'', freq:'',
            flavor:'', bpm: String(Math.round(40 + (energy-1)*10)), duration:'standard 2-3 min',
            arrangement:'', reverb:'', ambient:'', harmonic:'', brainwave:'',
            subject: act?.id || '', benefit:'', listener:'', session_len:'',
            title:'', channel:'', custom_tags:'', world: w.id, region: region === 'any' ? '' : region,
            platforms };
        }
      } else {
        input = { ...expert, flavor: expert.atmosphere,
          instAName: expert.instA, instBName: expert.instB, instCName: expert.instC,
          duration: 'standard 2-3 min',
          world: '', region: '',
          platforms };
      }
      setLastInput(input);
      const out = await generatePrompts(input);
      setResult(out); onAfterGenerate();
    } catch (e) { const ge = e as GenerateError; setError(ge.message || 'Something went wrong.'); }
    finally { setBusy(false); }
  }

  function copy(label: string, text: string) { navigator.clipboard.writeText(text); setCopied(label); setTimeout(() => setCopied(null), 1500); }

  function buildYouTubeContent(): string {
    if (!result || !lastInput) return '';
    const suno = result.platforms?.suno || result.suno || '';
    const mood = lastInput.mood || '';
    const bpm = lastInput.bpm || '';
    const scale = lastInput.scale || '';
    const instA = lastInput.instA || lastInput.instAName || '';
    const flavor = lastInput.flavor || '';
    const title = lastInput.title || '';
    const channel = lastInput.channel || '';
    const subject = lastInput.subject || '';
    const trackTitle = title ? title : scale ? `${scale} — ${mood.split(',')[0]} Instrumental` : `${mood.split(',')[0]} ${instA ? `${instA} ` : ''}Instrumental`;
    const desc = `${suno}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n🎵 TRACK INFO\n${scale ? `Scale / Raga: ${scale}` : ''}\n${instA ? `Lead Instrument: ${instA}` : ''}\n${bpm ? `Tempo: ${bpm} BPM` : ''}\n${subject ? `Best for: ${subject}` : ''}\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n📌 TIMESTAMPS\n00:00 Intro / Opening\n01:00 Main Theme\n03:00 Development\n05:00 Closing\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n${channel ? `Channel: ${channel}` : ''}\nGenerated with RaagEngine.com\n\nHASHTAGS:\n#${mood.split(',')[0].trim().replace(/\s+/g,'').toLowerCase()}music #instrumentalmusic #aimusic ${scale ? `#${scale.replace(/\s+/g,'').toLowerCase()}` : ''} ${instA ? `#${instA.split(' ')[0].replace(/\s+/g,'').toLowerCase()}` : ''} #meditationmusic #relaxingmusic`.trim();
    return `📺 SUGGESTED TITLE:\n${trackTitle}\n\n📝 DESCRIPTION:\n${desc}`;
  }

  function buildSocialContent(): string {
    if (!result || !lastInput) return '';
    const suno = result.platforms?.suno || result.suno || '';
    const mood = lastInput.mood || '';
    const instA = lastInput.instA || lastInput.instAName || '';
    const scale = lastInput.scale || '';
    const subject = lastInput.subject || '';
    const instagram = `✨ New track out now\n\n${mood.split(',')[0]} ${instA ? `${instA} ` : ''}${scale ? `· ${scale} ` : ''}instrumental\n\n${subject ? `Perfect for ${subject} 🎧` : ''}\n\nGenerated with @RaagEngine — link in bio\n\n#instrumentalmusic #aimusic #${mood.split(',')[0].trim().replace(/\s+/g,'').toLowerCase()}vibes`;
    const twitter = `🎵 Just generated this ${mood.split(',')[0]} instrumental${scale ? ` in ${scale}` : ''} with @RaagEngine\n\n${suno.substring(0, 200)}...\n\nTry it free → raagengine.com\n#AIMusic #InstrumentalMusic`;
    return `📸 INSTAGRAM:\n${instagram}\n\n🐦 TWITTER / X:\n${twitter}`;
  }

  function buildBinauralContent(): string {
    if (!result || !lastInput) return '';
    const brainwave = lastInput.brainwave || '';
    const freq = lastInput.freq || '';
    const bpm = lastInput.bpm || '';
    const suno = result.platforms?.suno || result.suno || '';
    if (!brainwave && !freq) return `🎧 BINAURAL BEAT SETUP\n\nNo brainwave or frequency was selected.\nRe-generate with Expert Mode → select a Brainwave State and Hz Tuning for binaural instructions.`;
    return `🎧 BINAURAL BEAT SETUP\n\nTRACK PROMPT:\n${suno}\n\nFREQUENCY SETTINGS:\n${brainwave ? `Brainwave Target: ${brainwave}` : ''}\n${freq ? `Hz Tuning: ${freq}` : ''}\n${bpm ? `Music Tempo: ${bpm} BPM` : ''}\n\nHOW TO APPLY IN YOUR DAW:\n1. Generate your track with the Suno prompt above\n2. Import into Audacity / GarageBand / Adobe Audition\n3. Add a binaural tone generator on a separate track\n4. Mix binaural tone at -18 to -22 dB under music\n5. Export in stereo — binaural requires headphones\n\nYOUTUBE DESCRIPTION TAG:\n"Best experienced with headphones. ${brainwave ? `Contains ${brainwave} binaural beats.` : ''} ${freq ? `Tuned to ${freq}.` : ''}"`;
  }

  function buildDistroContent(): string {
    if (!result || !lastInput) return '';
    const mood = lastInput.mood || '';
    const instA = lastInput.instA || lastInput.instAName || '';
    const scale = lastInput.scale || '';
    const bpm = lastInput.bpm || '';
    const flavor = lastInput.flavor || '';
    const title = lastInput.title || '';
    const channel = lastInput.channel || '';
    const subject = lastInput.subject || '';
    const trackTitle = title || `${scale || mood.split(',')[0]} ${instA ? `${instA.split(' ')[0]} ` : ''}Instrumental`;
    const artistName = channel || 'Your Artist Name';
    const primaryGenre = flavor.includes('Hindustani') || flavor.includes('Carnatic') || flavor.includes('Indian') ? 'World Music' : flavor.includes('Jazz') ? 'Jazz' : flavor.includes('Electronic') ? 'Electronic' : flavor.includes('Cinematic') ? 'Soundtrack' : 'World Music';
    return `📦 DISTROKID / SPOTIFY METADATA\n\nTrack Title: ${trackTitle}\nArtist Name: ${artistName}\nGenre: ${primaryGenre}\nSub-Genre: Instrumental\nYear: ${new Date().getFullYear()}\nLanguage: Instrumental (no lyrics)\nExplicit: No\n\nMood: ${mood.split(',').slice(0,2).join(', ')}\nTempo: ${bpm ? `${bpm} BPM` : 'Medium'}\nInstruments: ${instA || 'Various'}\n${scale ? `Scale/Mode: ${scale}` : ''}\nBest For: ${subject || 'Study, Meditation, Relaxation'}\n\nCopyright: ℗ ${new Date().getFullYear()} ${artistName}`;
  }

  async function onTabClick(tab: OutputTab) {
    setOutputTab(tab);
    if (tab === 'youtube' && !youtubeContent) { setTabBusy(true); setYoutubeContent(buildYouTubeContent()); setTabBusy(false); }
    if (tab === 'social' && !socialContent) { setTabBusy(true); setSocialContent(buildSocialContent()); setTabBusy(false); }
    if (tab === 'binaural' && !binauralContent) { setTabBusy(true); setBinauralContent(buildBinauralContent()); setTabBusy(false); }
    if (tab === 'distro' && !distroContent) { setTabBusy(true); setDistroContent(buildDistroContent()); setTabBusy(false); }
  }

  const canGenerate = !busy && !outOfQuota && (mode === 'feel' ? (!!emotionId || !!customMood.trim()) : true);

  // ── Step completion tracking ──────────────────────────────────────────────
  const feelSteps: boolean[] = [
    true,                                          // 1. Genre — always has default
    !!(emotionId || customMood.trim()),            // 2. Mood picked or typed
    !!activityId,                                  // 3. Activity chosen
    energy !== 5,                                  // 4. Slider moved from default
    platforms.length > 0,                          // 5. Platforms selected
    !busy && !!(emotionId || customMood.trim()),   // 6. Ready to generate
  ];
  const expertSteps: boolean[] = [
    !!expert.scale,                                // 1. Scale/Raga chosen
    expert.instA !== DEFAULT_INST_A || expert.instB !== DEFAULT_INST_B || expert.instC !== DEFAULT_INST_C, // 2. Any instrument changed
    !!(expert.bpm || expert.reverb),               // 3. BPM or reverb set
    !!(expert.brainwave || expert.freq),           // 4. Binaural (optional — green if set)
    platforms.length > 0,                          // 5. Platforms selected
    !busy && platforms.length > 0,                 // 6. Ready to generate
  ];
  const activeSteps = mode === 'feel' ? feelSteps : expertSteps;

  function InstSelect({ label, val, setVal, tooltip }: { label: string; val: string; setVal: (v: string) => void; tooltip?: string }) {
    return (
      <Field label={label} tooltip={tooltip}>
        <select className="re-input" value={val} onChange={e => setVal(e.target.value)} required>
          {Object.entries(instsByGroup).map(([g, list]) => (
            <optgroup key={g} label={g}>{list.map(i => <option key={i.n} value={i.v}>{i.n}</option>)}</optgroup>
          ))}
        </select>
      </Field>
    );
  }

  return (
    <div className="re-gen">
      <StepGuide mode={mode} steps={activeSteps} />
      {mode === 'feel' ? (
        <>
          <div className="re-panel">
            <div className="re-panel-h">How should the music feel?</div>
            <div className="re-panel-sub">Pick a mood, or type anything — a scene, a movie moment, a feeling, a proverb, a memory</div>

            {/* Genre */}
            <div className="re-label">
              🎵 Genre <FieldTooltip field="genre" />
            </div>
            <div className="re-chip-row">
              {FM2_WORLDS.map(w => (
                <button key={w.id} className={`re-chip ${world === w.id ? 'active' : ''}`} onClick={() => setWorld(w.id)}>{w.label}</button>
              ))}
            </div>

            {/* Region */}
            <div className="re-label re-label-mt">🌍 Region <span className="re-opt">(optional)</span></div>
            <select className="re-input" value={region} onChange={e => setRegion(e.target.value)}>
              {Object.entries(regionsByGroup).map(([group, list]) => (
                group === '—' ? list.map(r => <option key={r.value} value={r.value}>{r.label}</option>)
                : <optgroup key={group} label={group}>{list.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}</optgroup>
              ))}
            </select>

            <input className="re-input re-mt" placeholder='Type anything… "monsoon winds", "Interstellar docking scene"'
              value={customMood} onChange={e => setCustomMood(e.target.value)} />
          </div>

          {/* Emotion cards */}
          <div className="re-feel-grid">
            {FM2_EMOTIONS.map(e => (
              <button key={e.id} onClick={() => pickEmotion(emotionId === e.id ? null : e.id)}
                className={`re-feel-card ${emotionId === e.id ? 'selected' : ''}`}>
                <div className="re-feel-emoji">{e.emoji}</div>
                <div className="re-feel-tagline">{e.phrase}</div>
              </button>
            ))}
          </div>

          <div className="re-panel">
            <div className="re-panel-h">What will people do while listening?</div>
            <div className="re-panel-sub">This shapes the tempo, intensity, and arrangement</div>
            <div className="re-act-grid">
              {FM2_ACTIVITIES.map(a => (
                <button key={a.id} onClick={() => setActivityId(activityId === a.id ? null : a.id)}
                  className={`re-act-card ${activityId === a.id ? 'selected' : ''}`}>
                  <div className="re-act-emoji">{a.emoji}</div>
                  <div className="re-act-label">{a.label}</div>
                </button>
              ))}
            </div>

            {/* Calm/Intense slider */}
            <div className="re-energy-row">
              <span className="re-energy-end">🍃 Calm</span>
              <input type="range" min={1} max={10} value={energy} className="re-slider" onChange={e => setEnergy(parseInt(e.target.value))} />
              <span className="re-energy-end">🔥 Intense <b>{energy}</b></span>
            </div>
          </div>

          {/* Platform selector */}
          <div className="re-panel">
            <div className="re-panel-h">
              🎵 Generate prompts for: <FieldTooltip field="platform" />
            </div>
            <div className="re-plat-row">
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={() => togglePlatform(p.id)}
                  className={`re-plat-chip ${platforms.includes(p.id) ? 'active' : ''}`}>
                  {p.emoji} {p.name}{platforms.includes(p.id) && ' ✓'}
                </button>
              ))}
            </div>
            <div className="re-plat-note">Tap to toggle · <button className="re-linkbtn" onClick={() => setPlatforms(PLATFORMS.map(p => p.id))}>Select all</button> · <button className="re-linkbtn" onClick={() => setPlatforms([])}>Clear</button></div>
          </div>

          <button className="re-surprise" onClick={surprise}>🎲 Surprise Me — Random Everything</button>
        </>
      ) : (
        <>
          <h2 className="re-section-h">Expert Mode</h2>

          {/* Feel & Energy panel */}
          <div className="re-panel">
            <div className="re-panel-h"><span className="re-chip-mini">FEEL</span> Mood & Energy</div>
            <div className="re-form-grid">
              <Field label="Mood / Energy" tooltip="mood">
                <SelectInput v={expert.mood} set={v => setExpert(s => ({ ...s, mood: v }))}
                  opts={[['', '— Neutral / No specific mood —'],['meditative, peaceful','Meditative / Peaceful'],['devotional, sacred','Devotional / Sacred'],['romantic, tender','Romantic / Tender'],['melancholic, longing','Melancholic / Longing'],['mystical, ethereal','Mystical / Ethereal'],['joyful, uplifting','Joyful / Uplifting'],['cinematic, dramatic','Cinematic / Dramatic'],['lo-fi chill, creative flow','Lo-Fi / Creative Flow'],['energetic, festival, hype','Energetic / Festival'],['focus, creative flow','Focus / Flow'],['sleep, deep rest','Sleep / Deep Rest'],['dark, brooding','Dark / Brooding'],['nostalgic, warm','Nostalgic / Warm'],['triumphant, majestic','Triumphant / Majestic'],['haunting, ghostly','Haunting / Ghostly']]} />
              </Field>
              <Field label="BPM / Tempo" tooltip="bpm">
                <SelectInput v={expert.bpm} set={v => setExpert(s => ({ ...s, bpm: v }))}
                  opts={[['', 'Auto / AI decides'],['40','40 BPM (Delta / Deep Sleep)'],['50','50 BPM (Theta / Trance)'],['60','60 BPM (Slow Meditation)'],['70','70 BPM (Relaxed Ambient)'],['80','80 BPM (Gentle Flow)'],['90','90 BPM (Moderate Calm)'],['100','100 BPM (Focus Work)'],['110','110 BPM (Active Focus)'],['120','120 BPM (Energised)'],['130','130 BPM (Upbeat)'],['140','140 BPM (Driving Groove)'],['150','150 BPM (High Energy)'],['160','160 BPM (Psy-Trance)'],['174','174 BPM (Solfeggio Tempo)']]} />
              </Field>
              <Field label="Arrangement" tooltip="arrangement">
                <SelectInput v={expert.arrangement} set={v => setExpert(s => ({ ...s, arrangement: v }))}
                  opts={[['', '— Neutral —'],['sparse','Sparse'],['layered','Layered'],['driving','Driving'],['building','Building'],['gradual','Gradual']]} />
              </Field>
            </div>
          </div>

          {/* Sound panel */}
          <div className="re-panel">
            <div className="re-panel-h"><span className="re-chip-mini">SOUND</span> Instruments, Scale & Atmosphere</div>
            <div className="re-form-grid-3">
              <InstSelect label="Instrument A — Lead Melody" val={expert.instA} setVal={v => setExpert(s => ({ ...s, instA: v }))} tooltip="instrument_a" />
              <InstSelect label="Instrument B — Harmony / Support" val={expert.instB} setVal={v => setExpert(s => ({ ...s, instB: v }))} tooltip="instrument_b" />
              <InstSelect label="Instrument C — Rhythm / Texture" val={expert.instC} setVal={v => setExpert(s => ({ ...s, instC: v }))} tooltip="instrument_c" />

              <Field label="Scale / Raga" tooltip="raga">
                <select className="re-input" value={expert.scale} onChange={e => setExpert(s => ({ ...s, scale: e.target.value }))}>
                  <option value="">— Neutral / AI decides —</option>
                  {SCALE_GROUPS.map(sg => (
                    <optgroup key={sg.label} label={sg.label}>{sg.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}</optgroup>
                  ))}
                </select>
              </Field>

              <Field label="Atmosphere / Style">
                <select className="re-input" value={expert.atmosphere} onChange={e => setExpert(s => ({ ...s, atmosphere: e.target.value }))}>
                  <option value="">— Neutral —</option>
                  {ATMOSPHERES.map(ag => (
                    <optgroup key={ag.label} label={ag.label}>{ag.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}</optgroup>
                  ))}
                </select>
              </Field>

              <Field label="Reverb / Space" tooltip="reverb">
                <SelectInput v={expert.reverb} set={v => setExpert(s => ({ ...s, reverb: v }))}
                  opts={[['', 'Natural / Dry'],['Cave Reverb (Deep Echo)','Cave Reverb (Deep Echo)'],['Cathedral (Vast)','Cathedral (Vast)'],['Plate Reverb (Vintage)','Plate Reverb (Vintage)'],['Concert Hall','Concert Hall'],['Intimate Room','Intimate Room'],['Infinite Space','Infinite Space'],['Forest / Outdoor','Forest / Outdoor'],['Underwater','Underwater']]} />
              </Field>
            </div>
          </div>

          {/* Context panel */}
          <div className="re-panel">
            <div className="re-panel-h"><span className="re-chip-mini re-chip-green">CONTEXT</span> Purpose, Frequencies & Track Info</div>
            <div className="re-form-grid">
              <Field label="Primary Subject / What's this FOR?"><TextInput v={expert.subject} set={v => setExpert(s => ({ ...s, subject: v }))} ph="e.g. meditation, sleep, study" /></Field>
              <Field label="Secondary Benefit"><TextInput v={expert.benefit} set={v => setExpert(s => ({ ...s, benefit: v }))} ph="e.g. stress relief" /></Field>
              <Field label="Target Listener"><TextInput v={expert.listener} set={v => setExpert(s => ({ ...s, listener: v }))} ph="e.g. yoga practitioner" /></Field>
              <Field label="Session Length"><TextInput v={expert.session_len} set={v => setExpert(s => ({ ...s, session_len: v }))} ph="Don't specify / 15 min" /></Field>
            </div>

            <div className="re-subpanel">
              <div className="re-subpanel-h">
                🔊 Binaural Frequencies & Brainwave States <span className="re-opt">(optional)</span> <FieldTooltip field="binaural" />
              </div>
              <div className="re-form-grid">
                <Field label="Brainwave State" tooltip="binaural">
                  <SelectInput v={expert.brainwave} set={v => setExpert(s => ({ ...s, brainwave: v }))} opts={BRAINWAVES.map(b => [b.v, b.l] as [string,string])} />
                </Field>
                <Field label="Solfeggio / Hz Tuning">
                  <SelectInput v={expert.freq} set={v => setExpert(s => ({ ...s, freq: v }))} opts={FREQS.map(f => [f.v, f.l] as [string,string])} />
                </Field>
                <Field label="Ambient Sound Layer"><TextInput v={expert.ambient} set={v => setExpert(s => ({ ...s, ambient: v }))} ph="e.g. rain, ocean, forest" /></Field>
                <Field label="Harmonic Resonance"><TextInput v={expert.harmonic} set={v => setExpert(s => ({ ...s, harmonic: v }))} ph="e.g. warm, bright" /></Field>
              </div>
            </div>

            <div className="re-subpanel">
              <div className="re-subpanel-h">📝 Track Info & SEO</div>
              <div className="re-form-grid">
                <Field label="Track / Series Title"><TextInput v={expert.title} set={v => setExpert(s => ({ ...s, title: v }))} ph="e.g. Yaman Twilight" /></Field>
                <Field label="Artist / Channel Name"><TextInput v={expert.channel} set={v => setExpert(s => ({ ...s, channel: v }))} ph="e.g. your channel" /></Field>
                <Field label="Custom Tags" full><TextInput v={expert.custom_tags} set={v => setExpert(s => ({ ...s, custom_tags: v }))} ph="e.g. focus music, raag yaman, 528hz" /></Field>
                <Field label="Visual Scene — for Thumbnail Art" full>
                  <textarea className="re-input re-textarea" value={expert.scene} onChange={e => setExpert(s => ({ ...s, scene: e.target.value }))}
                    placeholder='e.g. A solitary musician playing bansuri on Varanasi ghats at golden hour...' rows={3} />
                </Field>
              </div>
            </div>
          </div>

          {/* Platform selector */}
          <div className="re-panel">
            <div className="re-panel-h">
              🎵 Generate prompts for: <FieldTooltip field="platform" />
            </div>
            <div className="re-plat-row">
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={() => togglePlatform(p.id)}
                  className={`re-plat-chip ${platforms.includes(p.id) ? 'active' : ''}`}>
                  {p.emoji} {p.name}{platforms.includes(p.id) && ' ✓'}
                </button>
              ))}
            </div>
            <div className="re-plat-note">Tap to toggle · <button className="re-linkbtn" onClick={() => setPlatforms(PLATFORMS.map(p => p.id))}>Select all</button> · <button className="re-linkbtn" onClick={() => setPlatforms([])}>Clear</button></div>
          </div>
        </>
      )}

      <div className="re-gen-bar">
        <div className="re-quota-note">
          ✓ Free &amp; unlimited — generate as many prompts as you like
        </div>
        <button className="re-btn-generate" disabled={!canGenerate} onClick={onGenerate}>
          {busy ? 'Generating…' : '⬡ Generate Full Package — All Modes & Platforms'}
        </button>
      </div>

      {error && <div className="re-error">{error}</div>}

      {result && (
        <>
          <div className="re-output-tabs">
            {(['prompts','youtube','social','binaural','distro','all'] as OutputTab[]).map(t => (
              <button key={t} className={`re-output-tab ${outputTab === t ? 'active' : ''}`} onClick={() => onTabClick(t)}>
                {t === 'prompts' ? '🎵 Prompts' : t === 'youtube' ? '▶ YouTube' : t === 'social' ? '✦ Social' : t === 'binaural' ? '🎧 Binaural' : t === 'distro' ? '📦 Distro' : '🗂 All'}
              </button>
            ))}
          </div>

          <div className="re-outputs">
            {outputTab === 'prompts' && (
              <>
                {platforms.includes('suno') && <Output label="Suno Prose" text={result.platforms?.suno || result.suno} copied={copied === 'suno'} onCopy={() => copy('suno', result.platforms?.suno || result.suno)} />}
                {platforms.includes('udio') && <Output label="Udio Prompt" text={result.platforms?.udio || result.udio} copied={copied === 'udio'} onCopy={() => copy('udio', result.platforms?.udio || result.udio)} />}
                {platforms.includes('suno') && <Output label="Suno Style Tag" text={result.styleTag} copied={copied === 'tag'} onCopy={() => copy('tag', result.styleTag)} />}
                {['stable','mureka','beatoven','soundraw','loudly','musicgen'].filter(p => platforms.includes(p) && result.platforms?.[p]).map(p => {
                  const label = { stable:'Stable Audio', mureka:'Mureka', beatoven:'Beatoven.ai', soundraw:'Soundraw', loudly:'Loudly', musicgen:'MusicGen' }[p] || p;
                  return <Output key={p} label={label} text={result.platforms![p]} copied={copied === p} onCopy={() => copy(p, result.platforms![p])} />;
                })}
              </>
            )}
            {outputTab === 'youtube' && (tabBusy ? <PlaceholderPanel title="YouTube SEO Package" desc="Building…" /> : youtubeContent ? <Output label="YouTube SEO Package" text={youtubeContent} copied={copied === 'youtube'} onCopy={() => copy('youtube', youtubeContent)} /> : <PlaceholderPanel title="YouTube SEO Package" desc="Click YouTube tab to generate." />)}
            {outputTab === 'social' && (tabBusy ? <PlaceholderPanel title="Social Captions" desc="Building…" /> : socialContent ? <Output label="Social Captions" text={socialContent} copied={copied === 'social'} onCopy={() => copy('social', socialContent)} /> : <PlaceholderPanel title="Social Captions" desc="Click Social tab to generate." />)}
            {outputTab === 'binaural' && (tabBusy ? <PlaceholderPanel title="Binaural Setup" desc="Building…" /> : binauralContent ? <Output label="Binaural Beat Setup" text={binauralContent} copied={copied === 'binaural'} onCopy={() => copy('binaural', binauralContent)} /> : <PlaceholderPanel title="Binaural Setup" desc="Click Binaural tab to generate." />)}
            {outputTab === 'distro' && (tabBusy ? <PlaceholderPanel title="Distro Metadata" desc="Building…" /> : distroContent ? <Output label="DistroKid / Spotify Metadata" text={distroContent} copied={copied === 'distro'} onCopy={() => copy('distro', distroContent)} /> : <PlaceholderPanel title="Distro Metadata" desc="Click Distro tab to generate." />)}
            {outputTab === 'all' && (
              <>
                <Output label="Suno Prose" text={result.platforms?.suno || result.suno} copied={copied === 'suno-all'} onCopy={() => copy('suno-all', result.platforms?.suno || result.suno)} />
                <Output label="Udio Prompt" text={result.platforms?.udio || result.udio} copied={copied === 'udio-all'} onCopy={() => copy('udio-all', result.platforms?.udio || result.udio)} />
                <Output label="Suno Style Tag" text={result.styleTag} copied={copied === 'tag-all'} onCopy={() => copy('tag-all', result.styleTag)} />
                {['stable','mureka','beatoven','soundraw','loudly','musicgen'].filter(p => result.platforms?.[p]).map(p => {
                  const label = { stable:'Stable Audio', mureka:'Mureka', beatoven:'Beatoven.ai', soundraw:'Soundraw', loudly:'Loudly', musicgen:'MusicGen' }[p] || p;
                  return <Output key={p} label={label} text={result.platforms![p]} copied={copied === p+'-all'} onCopy={() => copy(p+'-all', result.platforms![p])} />;
                })}
                {youtubeContent && <Output label="YouTube SEO" text={youtubeContent} copied={copied === 'yt-all'} onCopy={() => copy('yt-all', youtubeContent)} />}
                {socialContent && <Output label="Social Captions" text={socialContent} copied={copied === 'soc-all'} onCopy={() => copy('soc-all', socialContent)} />}
                {distroContent && <Output label="Distro Metadata" text={distroContent} copied={copied === 'dis-all'} onCopy={() => copy('dis-all', distroContent)} />}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

type Inst = { g: string; v: string; n: string; };

function Field({ label, children, full, tooltip }: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
  tooltip?: string;
}) {
  return (
    <label className={`re-field ${full ? 're-field-full' : ''}`}>
      <span className="re-field-label">
        {label}
        {tooltip && <FieldTooltip field={tooltip as any} />}
      </span>
      {children}
    </label>
  );
}

function TextInput({ v, set, ph }: { v:string; set:(v:string)=>void; ph?:string }) {
  return <input type="text" className="re-input" value={v} onChange={e => set(e.target.value)} placeholder={ph} />;
}

function SelectInput({ v, set, opts }: { v:string; set:(v:string)=>void; opts:Array<[string,string]> }) {
  return (
    <select className="re-input" value={v} onChange={e => set(e.target.value)}>
      {opts.map(([val,lab]) => <option key={val} value={val}>{lab}</option>)}
    </select>
  );
}

function Output({ label, text, copied, onCopy }: { label:string; text:string; copied:boolean; onCopy:()=>void }) {
  return (
    <div className="re-output">
      <div className="re-output-head">
        <span className="re-output-label">{label}</span>
        <button className="re-copy-btn" onClick={onCopy}>{copied ? '✓ Copied' : 'Copy'}</button>
      </div>
      <div className="re-output-text">{text}</div>
    </div>
  );
}

function PlaceholderPanel({ title, desc }: { title:string; desc:string }) {
  return (
    <div className="re-output">
      <div className="re-output-head"><span className="re-output-label">{title}</span></div>
      <div className="re-output-text" style={{fontStyle:'italic',color:'var(--re-text-3)'}}>{desc}</div>
    </div>
  );
}
